// The Notes view of PROJECT.md (decision 59): pure helpers that read and rewrite one section at a
// time, so a student can edit PROJECT.md as cards while it stays plain Markdown underneath. Every
// helper changes only the lines it is about and keeps everything else (other sections, comments,
// lines it doesn't understand) exactly as it was. The readers match parseProjectNotes in map.ts.

export interface NotesSection {
  /** The heading text, e.g. "Plan". */
  name: string;
  /** The whole heading line, e.g. "## Plan". */
  heading: string;
  /** Everything after the heading line up to the next "## " heading, newlines included. */
  body: string;
}

export interface NotesDoc {
  /** Text before the first "## " heading (usually "# PROJECT.md"). */
  preamble: string;
  sections: NotesSection[];
}

const HEADING = /^##\s.*$/gm;

/** Splits PROJECT.md into its "## " sections. Joining preamble + heading + body gives back the input exactly. */
export function readSections(md: string): NotesDoc {
  const starts = [...md.matchAll(HEADING)];
  const preamble = md.slice(0, starts[0]?.index ?? md.length);
  const sections = starts.map((m, i) => {
    const end = starts[i + 1]?.index ?? md.length;
    const heading = m[0];
    return { name: heading.replace(/^##\s+/, "").trim(), heading, body: md.slice(m.index! + heading.length, end) };
  });
  return { preamble, sections };
}

export function joinSections(doc: NotesDoc): string {
  return doc.preamble + doc.sections.map((s) => s.heading + s.body).join("");
}

const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** A section's text without the surrounding blank lines, or null when PROJECT.md has no such section. */
export function getSection(md: string, name: string): string | null {
  const s = readSections(md).sections.find((x) => same(x.name, name));
  return s ? s.body.replace(/^\s*\n/, "").trimEnd() : null;
}

/** The usual order of sections, used to put a missing section in a sensible place. */
export const NOTES_ORDER = ["About", "Plan", "Screens", "Rules", "Tests", "Dream", "Log"];

/** Replaces one section's text (or adds the section if it's missing) and leaves the rest of the file alone. */
export function replaceSection(md: string, name: string, text: string): string {
  const doc = readSections(md);
  const content = text.replace(/\r\n?/g, "\n").replace(/^\s*\n/, "").trimEnd();
  const i = doc.sections.findIndex((x) => same(x.name, name));
  if (i !== -1) {
    const last = i === doc.sections.length - 1;
    doc.sections[i]!.body = content ? `\n${content}\n${last ? "" : "\n"}` : last ? "\n" : "\n\n";
    return joinSections(doc);
  }
  // Missing: put it before the next section in the usual order, or at the end.
  const known = NOTES_ORDER.findIndex((n) => same(n, name));
  const title = known === -1 ? name.trim() : NOTES_ORDER[known]!;
  const before = known === -1 ? -1 : doc.sections.findIndex((x) => NOTES_ORDER.slice(known + 1).some((n) => same(n, x.name)));
  if (before !== -1) {
    doc.sections.splice(before, 0, { name: title, heading: `## ${title}`, body: content ? `\n${content}\n\n` : "\n\n" });
    return joinSections(doc);
  }
  const head = md.trimEnd();
  return `${head}${head ? "\n\n" : ""}## ${title}\n${content ? `${content}\n` : ""}`;
}

/** Applies a change to one section's text. */
export function updateSection(md: string, name: string, change: (text: string) => string): string {
  const now = getSection(md, name) ?? "";
  const next = change(now);
  return next === now && getSection(md, name) !== null ? md : replaceSection(md, name, next);
}

// ---- Lines and comments --------------------------------------------------------------------

/** The text with every HTML comment blanked out (newlines kept), so line numbers still line up. */
function blankComments(text: string): string {
  return text.replace(/<!--[\s\S]*?(?:-->|$)/g, (m) => m.replace(/[^\n]/g, " "));
}

/** The teacher's tips in a section: the text of its HTML comments. */
export function sectionHints(text: string): string[] {
  return [...text.matchAll(/<!--([\s\S]*?)(?:-->|$)/g)].map((m) => m[1]!.replace(/\s+/g, " ").trim()).filter(Boolean);
}

/** One line of text with no line breaks, safe to put on a Markdown line. */
const oneLine = (s: string) => s.replace(/[\r\n]+/g, " ").replace(/\s+/g, " ").trim();

// ---- Lists ---------------------------------------------------------------------------------
// "check": "- [ ] text" lines (Plan, Tests). "bullet": "- text" with an optional box (Screens).
// "line": any line that isn't blank or a comment (Rules, Log), the way parseProjectNotes reads Rules.

export type ItemKind = "check" | "bullet" | "line";

export interface NoteItem {
  text: string;
  done: boolean;
  /** Line number inside the section text. */
  line: number;
}

const CHECK = /^(\s*[-*]\s+\[)([ xX])(\]\s+)(.+)$/;
const BULLET = /^(\s*[-*]\s+)(?:\[([ xX])\]\s+)?(.+)$/;
const LABELLED = /^\s*(?:\*\*)?(For|Later)(?:\*\*)?\s*:/i;

export function listItems(text: string, kind: ItemKind): NoteItem[] {
  const shown = blankComments(text).split("\n");
  const items: NoteItem[] = [];
  shown.forEach((l, line) => {
    if (!l.trim()) return;
    if (kind === "check") {
      const m = CHECK.exec(l);
      if (m) items.push({ text: m[4]!.trim(), done: m[2] !== " ", line });
    } else if (kind === "bullet") {
      const m = BULLET.exec(l);
      if (m) items.push({ text: m[3]!.trim(), done: m[2] !== undefined && m[2] !== " ", line });
    } else {
      items.push({ text: l.replace(/^\s*[-*]\s+/, "").trim(), done: false, line });
    }
  });
  return items;
}

const newLine = (kind: ItemKind, text: string) => (kind === "line" ? `- ${oneLine(text)}` : `- [ ] ${oneLine(text)}`);

/** Adds an item after the last one (a new checklist line goes above a "Later:" line). */
export function addItem(text: string, kind: ItemKind, item: string): string {
  if (!oneLine(item)) return text;
  const lines = text ? text.split("\n") : [];
  const items = listItems(text, kind);
  const shown = blankComments(text).split("\n");
  let at = items.length ? items[items.length - 1]!.line + 1 : -1;
  if (at === -1 && kind === "check") {
    const later = shown.findIndex((l) => LABELLED.test(l) && /later/i.test(l));
    if (later !== -1) at = later;
  }
  if (at === -1) {
    // After everything else (comments included), skipping trailing blank lines.
    at = lines.length;
    while (at > 0 && !lines[at - 1]!.trim()) at--;
  }
  lines.splice(at, 0, newLine(kind, item));
  return lines.join("\n");
}

const withLine = (text: string, kind: ItemKind, index: number, change: (line: string) => string | null): string => {
  const item = listItems(text, kind)[index];
  if (!item) return text;
  const lines = text.split("\n");
  const next = change(lines[item.line]!);
  if (next === null) lines.splice(item.line, 1);
  else lines[item.line] = next;
  return lines.join("\n");
};

export function removeItem(text: string, kind: ItemKind, index: number): string {
  return withLine(text, kind, index, () => null);
}

export function setItemDone(text: string, kind: ItemKind, index: number, done: boolean): string {
  const mark = done ? "x" : " ";
  return withLine(text, kind, index, (l) => {
    const c = CHECK.exec(l);
    if (c) return `${c[1]}${c[2] !== " " && done ? c[2] : mark}${c[3]}${c[4]}`;
    const b = BULLET.exec(l);
    return b && kind !== "line" ? `${b[1]}[${mark}] ${b[3]}` : l;
  });
}

export function setItemText(text: string, kind: ItemKind, index: number, value: string): string {
  if (!oneLine(value)) return removeItem(text, kind, index);
  return withLine(text, kind, index, (l) => {
    const c = CHECK.exec(l);
    if (c && kind !== "line") return `${c[1]}${c[2]}${c[3]}${oneLine(value)}`;
    const b = BULLET.exec(l);
    if (b && kind === "bullet") return `${b[1]}${b[2] !== undefined ? `[${b[2]}] ` : ""}${oneLine(value)}`;
    const lead = /^\s*(?:[-*]\s+)?/.exec(l)![0];
    return `${lead || "- "}${oneLine(value)}`;
  });
}

/** Moves item `index` up (-1) or down (+1) by swapping it with its neighbour; nothing else moves. */
export function moveItem(text: string, kind: ItemKind, index: number, delta: -1 | 1): string {
  const items = listItems(text, kind);
  const a = items[index];
  const b = items[index + delta];
  if (!a || !b) return text;
  const lines = text.split("\n");
  [lines[a.line], lines[b.line]] = [lines[b.line]!, lines[a.line]!];
  return lines.join("\n");
}

// ---- Labelled lines: "For:" and "Later:" ----------------------------------------------------

const labelRe = (label: string) => new RegExp(`^\\s*(?:\\*\\*)?${label}(?:\\*\\*)?\\s*:\\s*(?:\\*\\*)?\\s*(.*)$`, "i");

/** The value of a "Label: value" line outside comments, or null. */
export function readLabel(text: string, label: string): string | null {
  const re = labelRe(label);
  for (const l of blankComments(text).split("\n")) {
    const m = re.exec(l);
    if (m) return m[1]!.trim();
  }
  return null;
}

/** Sets (or removes, when empty) a "Label: value" line, in place if there is one, else at the end. */
export function setLabel(text: string, label: string, value: string): string {
  const re = labelRe(label);
  const shown = blankComments(text).split("\n");
  const lines = text ? text.split("\n") : [];
  const at = shown.findIndex((l) => re.test(l));
  const v = oneLine(value);
  if (at !== -1) {
    if (v) lines[at] = `${label}: ${v}`;
    else lines.splice(at, 1);
    return lines.join("\n");
  }
  if (!v) return text;
  let end = lines.length;
  while (end > 0 && !lines[end - 1]!.trim()) end--;
  lines.splice(end, 0, `${label}: ${v}`);
  return lines.join("\n");
}

/** A starter's placeholder like "(who is this for?)" means "not filled in yet". */
export const isPlaceholder = (s: string) => /^\(.*\)$/.test(s.trim());

// ---- About and For: -------------------------------------------------------------------------

export interface AboutNotes {
  /** What the project is, without the For:/Later: lines or comments. */
  description: string;
  /** Who it's for, or "" when not filled in yet. */
  forWhom: string;
  /** The starter's hint for For:, like "who is this for?", if it has one. */
  forHint: string | null;
  /** The teacher's other tips in ## About. */
  hints: string[];
}

export function readAbout(md: string): AboutNotes {
  const about = getSection(md, "About") ?? "";
  const plan = getSection(md, "Plan") ?? "";
  // parseProjectNotes reads For: from About first, then Plan.
  const raw = readLabel(about, "For") ?? readLabel(plan, "For") ?? "";
  let forHint = isPlaceholder(raw) ? raw.slice(1, -1).trim() : null;
  const hints: string[] = [];
  for (const h of sectionHints(about)) {
    const m = /^For\s*:\s*(.*)$/i.exec(h);
    if (m && !forHint) forHint = m[1]!.replace(/^\((.*)\)$/, "$1").trim();
    else if (!m) hints.push(h);
  }
  const description = blankComments(about)
    .split("\n")
    .filter((l) => l.trim() && !LABELLED.test(l))
    .map((l) => l.trim())
    .join("\n");
  return { description, forWhom: raw && !isPlaceholder(raw) ? raw : "", forHint, hints };
}

/** Sets the For: line where parseProjectNotes will find it (About, else Plan, else a new line in About). */
export function setForWhom(md: string, value: string): string {
  const about = getSection(md, "About");
  if (about !== null && readLabel(about, "For") !== null) return replaceSection(md, "About", setLabel(about, "For", value));
  const plan = getSection(md, "Plan");
  if (plan !== null && readLabel(plan, "For") !== null) return replaceSection(md, "Plan", setLabel(plan, "For", value));
  return oneLine(value) ? replaceSection(md, "About", setLabel(about ?? "", "For", value)) : md;
}

/** Rewrites the description lines of ## About, keeping its For:/Later: lines and comments. */
export function setAboutText(md: string, value: string): string {
  const about = getSection(md, "About") ?? "";
  const shown = blankComments(about).split("\n");
  const lines = about ? about.split("\n") : [];
  const isText = (i: number) => !!shown[i]!.trim() && !LABELLED.test(shown[i]!);
  const first = lines.findIndex((_, i) => isText(i));
  const kept = lines.filter((_, i) => !isText(i));
  const add = value
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((l) => l.trimEnd())
    .filter((l) => l.trim());
  // New text goes where the old text started, or before For:/Later:, or after the comments.
  let at = first === -1 ? kept.findIndex((l) => LABELLED.test(blankComments(l))) : lines.slice(0, first).filter((_, i) => !isText(i)).length;
  if (at === -1) {
    at = kept.length;
    while (at > 0 && !kept[at - 1]!.trim()) at--;
  }
  kept.splice(at, 0, ...add);
  return replaceSection(md, "About", kept.join("\n"));
}
